import * as React from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { useQueries } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import LoaderService from '../../Services/LoaderService';
import { getDefaultCustomer, getUserId } from '../../Services/UserService';
import {
  isOnlinePanelOpenAtom,
  IVehicleOnlinePanels,
  onlineMapTemplatesAtom,
  onlinePanelAtom,
} from '../OnlinePanel/OnlinePanelState';
import { SocketConnectionWorker } from '../SocketConnection';
import { IOnlinePanelData } from '../OnlineMapNew/PanelsLayer/types';
import * as WidgetState from '../../store/recoil/widgets';
import { localForageStore } from '../../store/localForageStore';
import { IOption } from '../Forms/CmdField';
import useVisibilityChange from '../../hooks/useVisibilityChange';
import { IRide, IRidePosition } from '../RidePlayer/types';
import {
  ILastRideDistanceCumulative,
  IVehicle,
  IVehicleGroup,
  IVehicles,
  IVehiclesData,
  VehiclesBasicInfo,
} from '../Vehicles/types';
import { EVENT_USER_VEHICLES } from '../SocketConnection/constants';
import { selectedCustomersAtom } from '../../store/recoil/customers';
import {
  getVehiclesCustomerId,
  vehicleArrayToObject,
  vehicleDataArrayToObject,
  vehicleOnlinePanelArrayToObject,
} from '../../Services/VehicleService';
import { vehiclesDataAtom } from '../../store/recoil/vehiclesData';
import {
  lastRideDistanceCumulativeAtom,
  selectedVehiclesAtom,
  vehicleGroupsAtom,
  vehiclesAtom,
  vehiclesBasicInfoAtom,
  vehiclesOptionsAtom,
} from '../../store/recoil/vehicles';
import { getVehicleLastRide } from '../RidePlayer/RidePlayerService';
import { IWidget } from '../OnlinePanel/Widgets/types';
import {
  currentRidePositionForSelectedVehicleAtom,
  isRidePlayerOpenAtom,
  lastRideForSelectedVehicleAtom,
} from '../OnlineMapNew/OnlineMapState';
import { getVehicleGroups, getVehicles } from '../Navigator/NavigatorService';
import { isDemo } from '../../Services/DemoService';

function NavigatorData() {
  const isVisible = useVisibilityChange();
  // const previousIsVisible = usePrevious(isVisible, { jsonParse: false });
  const [vehiclesLoadedFirstTime, setVehiclesLoadedFirstTime] = React.useState<boolean>(false);
  const setVehiclesDataState = useSetRecoilState<IVehiclesData>(vehiclesDataAtom);
  const setVehicles = useSetRecoilState<IVehicles>(vehiclesAtom);
  const setVehiclesOptions = useSetRecoilState<IOption[]>(vehiclesOptionsAtom);
  const setSelectedVehicles = useSetRecoilState(selectedVehiclesAtom);
  const setUsedWidgets = useSetRecoilState<IWidget[]>(WidgetState.usedWidgets);
  const setVehicleGroups = useSetRecoilState<IVehicleGroup[]>(vehicleGroupsAtom);
  const [isVehiclesLoaded, setIsVehiclesLoaded] = React.useState<boolean>(false);
  const [isVehicleGroupsLoaded, setIsVehicleGroupsLoaded] = React.useState<boolean>(false);
  const selectedCustomers = useRecoilValue(selectedCustomersAtom);
  // const [isRideDataLoaded, setIsRideDataLoaded] = React.useState<boolean>(false);
  const setOnlinePanel = useSetRecoilState<IVehicleOnlinePanels>(onlinePanelAtom);
  const setOnlineMapTemplates = useSetRecoilState<IOnlinePanelData[]>(onlineMapTemplatesAtom);
  const [isRidePlayerOpen, setIsRidePlayerOpen] = useRecoilState(isRidePlayerOpenAtom);
  const setCurrentRidePositionForSelectedVehicle = useSetRecoilState<IRidePosition | null>(
    currentRidePositionForSelectedVehicleAtom
  );
  const [_lastRideForSelectedVehicle, setLastRideForSelectedVehicle] = useRecoilState<IRide | null>(
    lastRideForSelectedVehicleAtom
  );
  const [lastRideDistanceCumulative, setLastRideDistanceCumulative] =
    useRecoilState<ILastRideDistanceCumulative | null>(lastRideDistanceCumulativeAtom);
  const isOnlinePanelOpen = useRecoilValue(isOnlinePanelOpenAtom);
  const setVehiclesBasicInfo = useSetRecoilState<VehiclesBasicInfo>(vehiclesBasicInfoAtom);

  const results = useQueries({
    queries: [
      {
        queryKey: ['vehicleGroups'],
        queryFn: () => getVehicleGroups(),
        enabled: true,
        staleTime: 9000,
        gcTime: 0, // aka cacheTime
      },
      {
        queryKey: ['vehicles'],
        queryFn: () => getVehicles(),
        enabled: !isOnlinePanelOpen,
        staleTime: 9000,
        gcTime: 0,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
      },
      {
        queryKey: [`lastRide-${Number(isRidePlayerOpen)}`],
        queryFn: () => getVehicleLastRide(Number(isRidePlayerOpen)),
        enabled: !!isRidePlayerOpen,
        staleTime: 0,
        gcTime: 0,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
      },
    ],
  });

  const { data: vehicleGroups } = results[0];
  const {
    data: vehiclesList,
    isLoading: isVehiclesLoading,
    isFetching: isVehiclesFeching,
    isFetched: isVehiclesFeched,
  } = results[1];
  const {
    data: lastRide,
    isLoading: isLastRideLoading,
    // isFetched: isLastRideFetched,
    // isRefetching,
  } = results[2];

  useEffect(() => {
    if (isVehiclesLoaded && !isVehiclesFeching && isVehiclesFeched) {
      setIsVehiclesLoaded(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVehiclesFeching]);

  const defaultCustomers = getDefaultCustomer();

  useEffect(() => {
    if (isVehiclesLoading) {
      LoaderService.showNavigatorLoader();
    } else {
      LoaderService.showNavigatorLoader(false);
    }
  }, [isVehiclesLoading]);

  useEffect(() => {
    if (isLastRideLoading) {
      LoaderService.showLoader();
    } else {
      LoaderService.showLoader(false);
    }
  }, [isLastRideLoading]);

  useEffect(() => {
    if (vehicleGroups && !isVehicleGroupsLoaded) {
      setVehicleGroups(vehicleGroups);
      setIsVehicleGroupsLoaded(true);
    }
  }, [isVehicleGroupsLoaded, setVehicleGroups, vehicleGroups]);

  useEffect(() => {
    if (vehiclesList && !isVehiclesLoaded) {
      const vehiclesOptions = Object.values(vehiclesList.vehicles).map((i) => {
        return { value: String(i.id), item: `${i.licenceNumber} - ${i.name}` };
      });
      const vehiclesBasicInfo: VehiclesBasicInfo = {};
      vehiclesList.vehicles.forEach((i: IVehicle) => {
        vehiclesBasicInfo[i.id] = {
          id: i.id,
          name: i.name,
          licenceNumber: i.licenceNumber,
          vehicleTypeId: i.vehicleTypeId,
          customerId: i.customerId,
        };
      });
      setVehiclesOptions(vehiclesOptions);
      setVehicles(vehicleArrayToObject(vehiclesList.vehicles));
      setVehiclesDataState(vehicleDataArrayToObject(vehiclesList.vehiclesData));
      setUsedWidgets(vehiclesList.usedWidgets);
      setOnlineMapTemplates(vehiclesList.onlineMapTemplate);
      setOnlinePanel(vehicleOnlinePanelArrayToObject(vehiclesList.onlinePanelData));
      setVehiclesBasicInfo(vehiclesBasicInfo);

      if (defaultCustomers.length === 1) {
        const vehiclesByCustomer = getVehiclesCustomerId(
          vehicleArrayToObject(vehiclesList.vehicles),
          [],
          defaultCustomers[0]
        );
        if (vehiclesByCustomer.length === 1) {
          if (vehiclesByCustomer[0]) {
            setSelectedVehicles([vehiclesByCustomer[0].id]);
          }
        }
      }
      // I don't like this part of the code, but it's necessary for the demo
      if (isDemo() && !vehiclesLoadedFirstTime) {
        setSelectedVehicles(vehiclesList.vehicles.map((vehicle: IVehicle) => vehicle.id));
        setVehiclesLoadedFirstTime(true);
      }
      setIsVehiclesLoaded(true);
    }
  }, [
    defaultCustomers,
    isVehiclesLoaded,
    setOnlineMapTemplates,
    setOnlinePanel,
    setSelectedVehicles,
    setUsedWidgets,
    setVehicles,
    setVehiclesBasicInfo,
    setVehiclesDataState,
    setVehiclesOptions,
    vehiclesList,
    vehiclesLoadedFirstTime,
  ]);

  const handleLastRideDistanceCumulative = useCallback(
    (vehicleId: number, positions: IRidePosition[]) => {
      const lastPosition: IRidePosition =
        Object.values(positions)[Object.keys(positions).length - 1];
      setLastRideDistanceCumulative({
        ...lastRideDistanceCumulative,
        [vehicleId]: lastPosition.distanceCumulative,
      });
    },
    [lastRideDistanceCumulative, setLastRideDistanceCumulative]
  );

  // console.log('lastRide', lastRide && lastRide[0].rideData);
  // console.log('isRidePlayerOpen', isRidePlayerOpen);
  // console.log(
  //   'lastRideForSelectedVehicle:',
  //   lastRideForSelectedVehicle && lastRideForSelectedVehicle.rideData
  // );

  useEffect(() => {
    if (lastRide && isRidePlayerOpen && isVisible === true) {
      if (lastRide && lastRide[0]) {
        const vehicleRide: IRide = {
          positions: lastRide[0].positions,
          rideData: lastRide[0].rideData,
        };
        setCurrentRidePositionForSelectedVehicle(null);
        setLastRideForSelectedVehicle(vehicleRide);
        handleLastRideDistanceCumulative(Number(isRidePlayerOpen), lastRide[0].positions);
      }
    }
    if (lastRide === false) {
      setCurrentRidePositionForSelectedVehicle(null);
      setLastRideForSelectedVehicle(null);
      setIsRidePlayerOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isRidePlayerOpen,
    lastRide,
    // handleLastRideDistanceCumulative,
    // isLastRideLoading,
    // isRefetching,
    // isRideDataLoaded,
    // isRidePlayerOpen,
    // isVisible,
    // lastRide,
    // previousIsVisible,
    // setCurrentRidePositionForSelectedVehicle,
    // setLastRideForSelectedVehicle,
  ]);

  useEffect(() => {
    if (vehiclesList) {
      // Send user vehicles to SocketServer
      const vehicleIds = vehiclesList.vehicles.map((vehicle: IVehicle) => vehicle.id);
      const data = {
        event: EVENT_USER_VEHICLES,
        payload: vehicleIds,
        userId: getUserId(),
      };
      SocketConnectionWorker.postMessage(data);
      localForageStore.setItem('vehicleIds', vehicleIds);
    }
  }, [selectedCustomers, vehiclesList]);

  return null;
}

export default NavigatorData;
